import { useState } from "react";
import { useAuth } from "../contexts/AuthContext";
import { Navigate, useNavigate } from "react-router-dom";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { getMasterKey, deriveKEK, setMasterKey } from "../lib/crypto";
import { toast } from "sonner";
import naclUtil from "tweetnacl-util";
import nacl from "tweetnacl";
import { Eye, EyeOff } from "lucide-react";

export default function UnlockPage() {
  const { user, logout } = useAuth();
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const navigate = useNavigate();

  // If not logged in, or already unlocked, redirect away
  if (!user) return <Navigate to="/auth" replace />;
  if (getMasterKey()) return <Navigate to="/" replace />;

  const handleUnlock = async (e) => {
    e.preventDefault();
    if (!password) return;
    setLoading(true);

    try {
      // 1. Derive KEK from password
      const kek = await deriveKEK(password, user.email);

      // 2. Decrypt Master Key
      const data = naclUtil.decodeBase64(user.encrypted_master_key);
      const nonce = data.slice(0, 24);
      const ciphertext = data.slice(24);
      const mk = nacl.secretbox.open(ciphertext, nonce, kek);

      if (!mk) throw new Error("Incorrect password.");

      // 3. Load into memory
      setMasterKey(mk);

      // 4. Proceed
      toast.success("Vault unlocked.");
      navigate("/");
    } catch (err) {
      toast.error(err.message || "Failed to unlock vault.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4 bg-background">
      <div className="w-full max-w-sm">
        <h1 className="font-serif-journal text-3xl mb-2 text-center">Unlock Vault</h1>
        <p className="meta-label text-center text-muted-foreground mb-8">
          Welcome back. Enter your password to unlock your encryption keys.
        </p>

        <form onSubmit={handleUnlock} className="space-y-4">
          <div className="relative">
            <Input
              type={showPassword ? "text" : "password"}
              placeholder="Password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              disabled={loading}
              className="pr-10"
              autoFocus
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
              tabIndex="-1"
            >
              {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            </button>
          </div>
          <Button type="submit" className="w-full" disabled={loading || !password}>
            {loading ? "Unlocking..." : "Unlock"}
          </Button>
        </form>

        <div className="mt-6 text-center">
          <button 
            onClick={logout} 
            className="meta-label text-xs text-muted-foreground hover:text-foreground"
          >
            Not you? Sign out
          </button>
        </div>
      </div>
    </div>
  );
}
